import { Link, useSearchParams } from "react-router-dom";
import { formatDate, formatDateTime } from "../../api/dates";
import { EVIDENCE_LABEL, useOverrideWeek } from "../../api/readiness";
import { StatusChip } from "../../components/ui";
import "../../styles/routing.css";
import RoutingTabs from "./RoutingTabs";

const PO_LABEL: Record<string, string> = { awaiting: "Awaiting", received: "Received", validated: "Validated" };

/** Every emergency override granted in one week, oldest first: who granted it,
 * why, the evidence, the plan that holds the site and where its City PO stands
 * now (WS3 scope item 3: "reported weekly"). An override granted in an earlier
 * week and still in force is listed too: it lasts until it is revoked. */
export default function OverridesPage() {
  const [params, setParams] = useSearchParams();
  const view = useOverrideWeek(params.get("week"));
  const data = view.data;
  return (
    <div className="page-bleed routing-page">
      <RoutingTabs />
      <div className="routing-content">
        <header className="schedule-head">
          <h1>{data ? `Emergency overrides · week of ${formatDate(data.monday)}` : "Emergency overrides"}</h1>
          {data ? (
            <div className="schedule-nav">
              <button type="button" className="btn" onClick={() => setParams({ week: data.prev_week })}>
                ‹ Previous
              </button>
              <button type="button" className="btn" onClick={() => setParams({})}>
                This week
              </button>
              <button type="button" className="btn" onClick={() => setParams({ week: data.next_week })}>
                Next ›
              </button>
            </div>
          ) : null}
        </header>
        {view.isPending ? <p className="empty-state">Loading the overrides…</p> : null}
        {view.isError ? <p className="notice bad">The overrides could not be loaded: {view.error.message}</p> : null}
        {data ? (
          <>
            <p className="muted-note">{data.active_total} in force in all</p>
            {data.rows.length === 0 ? (
              <p className="empty-state">No emergency override was granted this week, and none is in force.</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Granted</th>
                    <th>Site</th>
                    <th>Reason</th>
                    <th>Evidence</th>
                    <th>Scheduled</th>
                    <th>City PO</th>
                    <th>State</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        {formatDateTime(row.granted_at)}
                        <span className="sub">{row.granted_by ?? "—"}</span>
                      </td>
                      <td>
                        <Link to={`/sites/${row.site.id}`}>{row.site.name}</Link>
                        <span className="sub mono">{[row.site.notif_no, row.site.rs_no].filter(Boolean).join(" · ")}</span>
                      </td>
                      <td>{row.reason}</td>
                      <td>
                        <a href={row.document_url} target="_blank" rel="noreferrer">
                          {EVIDENCE_LABEL[row.evidence_kind]}
                        </a>
                      </td>
                      <td>
                        {row.scheduled ? (
                          <>
                            <Link to={`/routing/plans/${row.scheduled.plan_id}`}>{row.scheduled.plan_name}</Link>
                            <span className="sub">{formatDate(row.scheduled.date)}</span>
                          </>
                        ) : (
                          "Not yet"
                        )}
                      </td>
                      <td>{PO_LABEL[row.po_status] ?? row.po_status}</td>
                      <td>
                        {row.active ? (
                          <StatusChip tone="warn">In force</StatusChip>
                        ) : (
                          <StatusChip tone="quiet">{`Revoked ${formatDate(row.revoked_at)}`}</StatusChip>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
